import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { FocalArea } from '../models/focal-area.model';
import { AREA_TYPE_OPTIONS, CROP_MODE_OPTIONS } from '../models/focal-area.constants';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-focal-area-sidebar',
  templateUrl: './focal-area-sidebar.component.html',
  styleUrls: ['./focal-area-sidebar.component.scss']
})
export class FocalAreaSidebarComponent implements AfterViewChecked, OnChanges {
  constructor(private dialog: MatDialog) {}
  @ViewChild('nameInput') nameInput?: ElementRef<HTMLInputElement>;

  @Input() focalAreas: FocalArea[] = [];
  @Input() selectedIndex: number | null = null;
  @Input() isSaving: boolean = false;
  @Input() isLoadingImage: boolean = false;
  @Input() loadImageError: string | null = null;
  @Input() isAspectRatioDirty: ((index: number) => boolean) | null = null;
  @Input() editingNameIndex: number | null = null; // Track which focal area name is being edited
  @Input() selectMode: boolean = false;
  @Input() canSave: boolean = false;
  @Input() isDirtyFocalArea: ((index: number) => boolean) | null = null;
  @Input() tagFilterSelection: string[] = [];
  @Input() shouldShowAutoGenerateButton: boolean = false;

  @Output() addFocalArea = new EventEmitter<void>();
  @Output() selectFocalArea = new EventEmitter<number>();
  @Output() updateFocalArea = new EventEmitter<{ index: number; focalArea: Partial<FocalArea>; isManualAspectRatioEdit?: boolean }>();
  @Output() deleteFocalArea = new EventEmitter<number>();
  @Output() resetAspectRatio = new EventEmitter<number>();
  @Output() save = new EventEmitter<void>();
  @Output() copyCropData = new EventEmitter<void>();
  @Output() pasteCropData = new EventEmitter<void>();
  @Output() replaceImage = new EventEmitter<void>();
  @Output() loadImageFromUrl = new EventEmitter<string>();
  @Output() copySingleCropData = new EventEmitter<number>();
  @Output() startEditingName = new EventEmitter<number>();
  @Output() finishEditingName = new EventEmitter<void>();
  @Output() selectFocalAreaForTarget = new EventEmitter<number>();
  @Output() tagFilterSelectionChange = new EventEmitter<string[]>();
  @Output() copyFocalAreas = new EventEmitter<void>();
  @Output() pasteFocalAreas = new EventEmitter<void>();
  @Output() autoGenerateFocalAreas = new EventEmitter<void>();

  areaTypeOptions = AREA_TYPE_OPTIONS;
  cropModeOptions = CROP_MODE_OPTIONS;
  uniqueTags: string[] = [];
  tagFilterSelections: Set<string> = new Set();
  private shouldFocusInput = false;
  private lastEmittedTags: string[] = [];
  editingAspectRatioIndex: number | null = null;
  tempAspectRatio: string = '';
  editingCoordinateIndex: number | null = null;
  editingCoordinateType: 'x' | 'y' | 'width' | 'height' | null = null;
  tempCoordinate: string = '';
  editingTagsIndex: number | null = null;
  tempTags: string[] = [];
  tagInputValue = '';
  tagEditError: string | null = null;
  tempName: string = ''; // Temporary name for editing to prevent focus loss
  private justStartedEditing: boolean = false; // Flag to prevent immediate blur after starting edit

  onAddClick(): void {
    this.addFocalArea.emit();
  }

  onSelectClick(index: number): void {
    this.selectFocalArea.emit(index);
  }

  onSetDefault(index: number): void {
    this.updateFocalArea.emit({
      index,
      focalArea: { isDefault: true }
    });
  }

  onAreaTypeChange(index: number, areaType: string): void {
    this.updateFocalArea.emit({
      index,
      focalArea: { areaType }
    });
  }

  onCropModeChange(index: number, cropMode: 'cover' | 'contain'): void {
    this.updateFocalArea.emit({
      index,
      focalArea: { cropMode }
    });
  }

  onToggleCropMode(index: number, event: Event): void {
    event.stopPropagation();
    const currentMode = this.focalAreas[index]?.cropMode || 'contain';
    const newMode = currentMode === 'contain' ? 'cover' : 'contain';
    this.onCropModeChange(index, newMode);
  }

  onAspectRatioChange(index: number, aspectRatio: string): void {
    this.updateFocalArea.emit({
      index,
      focalArea: { aspectRatio },
      isManualAspectRatioEdit: true // Mark as manual edit from user input
    });
  }

  onResetAspectRatioClick(index: number, event: Event): void {
    event.stopPropagation(); // Prevent triggering other events
    this.resetAspectRatio.emit(index);
  }

  isEditingAspectRatio(index: number): boolean {
    return this.editingAspectRatioIndex === index;
  }

  onStartEditingAspectRatio(index: number, event: Event): void {
    event.stopPropagation();
    this.editingAspectRatioIndex = index;
    this.tempAspectRatio = this.focalAreas[index]?.aspectRatio || '1:1';
  }

  onSaveAspectRatio(index: number): void {
    if (this.tempAspectRatio.trim()) {
      this.onAspectRatioChange(index, this.tempAspectRatio.trim());
    }
    this.editingAspectRatioIndex = null;
    this.tempAspectRatio = '';
  }

  onCancelEditingAspectRatio(): void {
    this.editingAspectRatioIndex = null;
    this.tempAspectRatio = '';
  }

  onAspectRatioInputKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onSaveAspectRatio(index);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.onCancelEditingAspectRatio();
    }
  }

  async onDeleteClick(index: number): Promise<void> {
    const focalArea = this.focalAreas[index];
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        title: 'Delete Focal Area',
        message: `Are you sure you want to delete "${focalArea?.name || 'this focal area'}"?`,
        confirm: 'Delete',
        cancel: 'Cancel',
        remove: true
      }
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      this.deleteFocalArea.emit(index);
    }
  }

  onSaveClick(): void {
    this.save.emit();
  }

  onCopyClick(): void {
    this.copyCropData.emit();
  }

  onPasteClick(): void {
    this.pasteCropData.emit();
  }

  onReplaceImageClick(): void {
    this.replaceImage.emit();
  }

  onCopyCropData(index: number): void {
		// Copy specific focal area to clipboard
		const focalArea = this.focalAreas[index];
		if (focalArea) {
			const copyObject = { focalAreas: [focalArea] };

			const data = JSON.stringify(copyObject, null, 2);
			navigator.clipboard.writeText(data);
		}
  }

  isSelected(index: number): boolean {
    return this.selectedIndex === index;
  }

  isEditingName(index: number): boolean {
    return this.editingNameIndex === index;
  }

  ngAfterViewChecked(): void {
    // Auto-focus and select the input when editing mode is activated
    if (this.shouldFocusInput && this.nameInput) {
      this.nameInput.nativeElement.focus();
      this.nameInput.nativeElement.select();
      this.shouldFocusInput = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['focalAreas']) {
      this.uniqueTags = this.buildUniqueTags(this.focalAreas);
      this.applyInputSelections();
      this.emitTagFilters();
    }

    if (changes['tagFilterSelection'] && !changes['tagFilterSelection'].firstChange) {
      this.applyInputSelections();
    }
  }

  onStartEditingName(index: number, event: Event): void {
    event.stopPropagation();
    this.shouldFocusInput = true;
    this.justStartedEditing = true;
    // Store the current name in temp variable
    this.tempName = this.focalAreas[index]?.name || '';
    this.startEditingName.emit(index);

    // Clear the flag after a short delay to allow the input to gain focus
    setTimeout(() => {
      this.justStartedEditing = false;
    }, 200);
  }

  onNameInputBlur(): void {
    // Don't save immediately if we just started editing (prevents closing when clicking after "Rename")
    if (this.justStartedEditing) {
      return;
    }
    // Save the name when losing focus
    this.saveName();
  }

  onNameInputKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveName();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      // Cancel editing without saving
      this.finishEditingName.emit();
    }
  }

  private saveName(): void {
    if (this.editingNameIndex !== null && this.tempName.trim()) {
      this.updateFocalArea.emit({
        index: this.editingNameIndex,
        focalArea: { name: this.tempName.trim() }
      });
    }
    this.finishEditingName.emit();
  }

  isEditingTags(index: number): boolean {
    return this.editingTagsIndex === index;
  }

  onStartEditingTags(index: number, event: Event): void {
    event.stopPropagation();
    this.editingTagsIndex = index;
    this.tempTags = [...(this.focalAreas[index]?.tags || [])];
    this.tagInputValue = '';
    this.tagEditError = null;
  }

  onCancelEditingTags(event?: Event): void {
    event?.stopPropagation();
    this.editingTagsIndex = null;
    this.tempTags = [];
    this.tagInputValue = '';
    this.tagEditError = null;
  }

  onSaveTags(index: number, event?: Event): void {
    event?.stopPropagation();
    const sanitized: string[] = [];
    const seen = new Set<string>();
    for (const rawTag of this.tempTags) {
      const trimmed = rawTag.trim();
      if (!trimmed) {
        continue;
      }
      const key = trimmed.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      sanitized.push(trimmed);
    }
    this.updateFocalArea.emit({
      index,
      focalArea: { tags: sanitized }
    });
    this.onCancelEditingTags();
  }

  onTagInputValueChange(value: string): void {
    this.tagInputValue = value;
    this.tagEditError = null;
  }

  onTagInputKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleTagCandidate(this.tagInputValue.trim(), index);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.onCancelEditingTags();
    }
  }

  onRemoveTempTag(tagIndex: number): void {
    this.tempTags.splice(tagIndex, 1);
    this.tempTags = [...this.tempTags];
  }

  onAddTagClick(index: number, event?: Event): void {
    event?.stopPropagation();
    this.handleTagCandidate(this.tagInputValue.trim(), index);
  }

  onToggleFilterTag(tag: string): void {
    if (this.tagFilterSelections.has(tag)) {
      this.tagFilterSelections.delete(tag);
    } else {
      this.tagFilterSelections.add(tag);
    }

    this.emitTagFilters();
  }

  onClearTagFilters(): void {
    if (this.tagFilterSelections.size === 0) {
      return;
    }

    this.tagFilterSelections.clear();
    this.emitTagFilters();
  }

  private handleTagCandidate(candidate: string, index: number): void {
    if (!this.isEditingTags(index) || !candidate) {
      this.tagInputValue = '';
      return;
    }

    const isValid = /^[A-Za-z0-9_-]+$/.test(candidate);
    if (!isValid) {
      this.tagEditError = 'Tags may only include letters, numbers, hyphen, or underscore.';
      this.tagInputValue = '';
      return;
    }

    const exists = this.tempTags.some(tag => tag.toLowerCase() === candidate.toLowerCase());
    if (exists) {
      this.tagEditError = 'Tag already added.';
      this.tagInputValue = '';
      return;
    }

    this.tempTags = [...this.tempTags, candidate];
    this.tagInputValue = '';
    this.tagEditError = null;
  }

  private buildUniqueTags(focalAreas: FocalArea[]): string[] {
    const seen = new Set<string>();
    for (const area of focalAreas || []) {
      for (const rawTag of area.tags || []) {
        if (typeof rawTag !== 'string') {
          continue;
        }
        const trimmed = rawTag.trim();
        if (!trimmed) {
          continue;
        }
        seen.add(trimmed);
      }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }

  private applyInputSelections(): void {
    const allowed = new Set(this.uniqueTags);
    const initial = this.tagFilterSelection || [];
    this.tagFilterSelections = new Set(initial.filter(tag => allowed.has(tag)));
  }

  private emitTagFilters(): void {
    const next = Array.from(this.tagFilterSelections);

    // Only emit if tags have actually changed
    const tagsChanged = JSON.stringify(this.lastEmittedTags) !== JSON.stringify(next);

    if (tagsChanged) {
      this.lastEmittedTags = next;
      this.tagFilterSelectionChange.emit(next);
    }
  }

  onSelectFocalAreaClick(index: number): void {
    this.selectFocalAreaForTarget.emit(index);
  }

  isDirty(index: number): boolean {
    return this.isDirtyFocalArea ? this.isDirtyFocalArea(index) : false;
  }

  // Coordinate editing methods
  isEditingCoordinate(index: number, type: 'x' | 'y' | 'width' | 'height'): boolean {
    return this.editingCoordinateIndex === index && this.editingCoordinateType === type;
  }

  onStartEditingCoordinate(index: number, type: 'x' | 'y' | 'width' | 'height', event: Event): void {
    event.stopPropagation();
    this.editingCoordinateIndex = index;
    this.editingCoordinateType = type;
    const focalArea = this.focalAreas[index];
    if (focalArea) {
      this.tempCoordinate = focalArea.boundingBox[type].toString();
    }
  }

  onSaveCoordinate(index: number): void {
    if (this.tempCoordinate !== '' && this.editingCoordinateType) {
      const value = typeof this.tempCoordinate === 'string'
        ? parseFloat(this.tempCoordinate)
        : this.tempCoordinate;
      if (!isNaN(value)) {
        const update: Partial<FocalArea> = {
          boundingBox: {
            ...this.focalAreas[index].boundingBox,
            [this.editingCoordinateType]: value
          }
        };
        this.updateFocalArea.emit({
          index,
          focalArea: update
        });
      }
    }
    this.editingCoordinateIndex = null;
    this.editingCoordinateType = null;
    this.tempCoordinate = '';
  }

  onCancelEditingCoordinate(): void {
    this.editingCoordinateIndex = null;
    this.editingCoordinateType = null;
    this.tempCoordinate = '';
  }

  onCoordinateInputKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onSaveCoordinate(index);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.onCancelEditingCoordinate();
    }
  }

  onCopyFocalAreasClick(): void {
    this.copyFocalAreas.emit();
  }

  onPasteFocalAreasClick(): void {
    this.pasteFocalAreas.emit();
  }

  onAutoGenerateFocalAreasClick(): void {
    this.autoGenerateFocalAreas.emit();
  }
}
